/**
 * A button that asks before it changes anything, then runs once (admin-design-r1, `ConfirmAction`). With a
 * `question`, it asks first: what happens, in a sentence, with a Yes and a Cancel. Without one it runs on the click,
 * for safe actions. `working` shows a spinner in its place; `children` is the slot for what happened.
 */
import { useState, type ReactNode } from 'react'

export function ConfirmAction({
  label,
  question,
  yes,
  onConfirm,
  disabled,
  reason,
  working,
  tone = 'soft',
  children,
}: {
  label: string
  question?: string
  /** The confirm button's words; defaults to the label. */
  yes?: string
  onConfirm: () => void
  disabled?: boolean
  /** Why it's disabled, beside the button. */
  reason?: string | null
  working?: boolean
  tone?: 'soft' | 'ink' | 'ghost'
  children?: ReactNode
}) {
  const [asking, setAsking] = useState(false)
  if (working) {
    return (
      <div className="act" role="status">
        <span className="spin-sm" aria-hidden="true" />
        <span>{label}: working…</span>
      </div>
    )
  }
  if (asking && question) {
    return (
      <div className="act act-ask" role="group" aria-label={`Confirm: ${label}`}>
        <p>{question}</p>
        <span className="row-btns">
          <button type="button" className="btn btn-ink btn-xs" onClick={() => (setAsking(false), onConfirm())}>
            {yes ?? label}
          </button>
          <button type="button" className="btn btn-ghost btn-xs" onClick={() => setAsking(false)} autoFocus>
            Cancel
          </button>
        </span>
      </div>
    )
  }
  return (
    <div className="act">
      <button type="button" className={`btn btn-${tone} btn-xs`} disabled={disabled} onClick={() => (question ? setAsking(true) : onConfirm())}>
        {label}
      </button>
      {disabled && reason && <span className="act-why">{reason}</span>}
      {children}
    </div>
  )
}
